// Thin Nexus API wrapper. The runner makes only two calls:
//   1) GET  /api/tenant/aletheia/missions/{public_id}
//      → returns the published mission + its current revision payload.
//   2) POST /api/tenant/aletheia/attempts
//      → idempotent attempt submission.
//
// Auth: the host (native shell) hands us a short-lived JWT via the
// `?token=` URL query parameter. We forward it as Bearer on every request.
// Production will likely migrate to a per-mission ticket exchange so the
// JWT never lives in URL params — tracked as a TODO.

export interface AletheiaConfig {
  baseUrl: string;
  tenantId: string;
  token: string | null;
}

export function readConfigFromQuery(search: string): {
  config: AletheiaConfig;
  missionId: string | null;
  revisionId: string | null;
} {
  const params = new URLSearchParams(search);
  const baseUrl =
    params.get("nexus_url") ||
    // Default: same hostname used by aletheia-swift's nexusBaseURL fallback.
    "http://ahimsa.localhost:8000";
  const tenantId = params.get("tenant") || "mx";
  const token = params.get("token");
  const missionId = params.get("mission_id");
  const revisionId = params.get("revision_id");
  return {
    config: { baseUrl, tenantId, token },
    missionId,
    revisionId,
  };
}

interface RequestOpts {
  method?: "GET" | "POST";
  body?: unknown;
}

async function request<T>(
  config: AletheiaConfig,
  path: string,
  opts: RequestOpts = {}
): Promise<T> {
  const url = new URL(path, config.baseUrl).toString();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Tenant-ID": config.tenantId,
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (config.token) headers["Authorization"] = `Bearer ${config.token}`;

  const resp = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Nexus ${resp.status} on ${path}: ${text.slice(0, 300)}`);
  }
  return (await resp.json()) as T;
}

// ---- Types we actually depend on (subset of generated OpenAPI). ------------

export interface MissionRevisionDTO {
  id: string;
  version: number;
  schema_version: string;
  payload: unknown;
  is_published: boolean;
}

export interface MissionDTO {
  public_id: string;
  slug: string;
  title: string;
  activity_kind: string;
  status: string;
  current_revision?: MissionRevisionDTO | null;
  revisions?: MissionRevisionDTO[];
}

export async function fetchMission(
  config: AletheiaConfig,
  missionId: string
): Promise<MissionDTO> {
  return request<MissionDTO>(config, `/api/tenant/aletheia/missions/${missionId}`);
}

export async function postAttempt(
  config: AletheiaConfig,
  body: {
    client_event_id: string;
    session_id?: string;
    mission_id: string;
    mission_revision_id: string;
    started_at: string;
    ended_at: string;
    duration_ms: number;
    outcome: "completed" | "partial" | "failed" | "abandoned";
    score: number;
    client_payload: unknown;
  }
): Promise<unknown> {
  return request(config, `/api/tenant/aletheia/attempts`, {
    method: "POST",
    body,
  });
}
